/**
 * Operator API client — fetch with one silent access-token refresh on 401.
 * On refresh failure it clears the session; ProtectedRoute then redirects to login.
 */
import { i18n } from '@/lib/i18n'
import { useAuthStore } from '@/stores/authStore'

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4000'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly messageKey: string,
    readonly issues?: unknown,
  ) {
    super(messageKey)
    this.name = 'ApiError'
  }
}

async function toApiError(res: Response): Promise<ApiError> {
  let code = 'internal'
  let messageKey = 'errors.generic'
  let issues: unknown
  try {
    const body = (await res.json()) as { code?: string; message_key?: string; issues?: unknown }
    code = body.code ?? code
    messageKey = body.message_key ?? messageKey
    issues = body.issues
  } catch {
    /* non-JSON body */
  }
  return new ApiError(res.status, code, messageKey, issues)
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  auth?: boolean
}

function doFetch(path: string, opts: RequestOptions, token: string | null): Promise<Response> {
  return fetch(`${API_URL}${path}`, {
    method: opts.method ?? 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': i18n.language,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
  })
}

let refreshPromise: Promise<boolean> | null = null

async function tryRefresh(): Promise<boolean> {
  const { refreshToken } = useAuthStore.getState()
  if (!refreshToken) return false
  refreshPromise ??= fetch(`${API_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
    .then(async (res) => {
      if (!res.ok) return false
      const data = (await res.json()) as { access_token: string; refresh_token: string }
      useAuthStore.getState().setTokens(data.access_token, data.refresh_token)
      return true
    })
    .catch(() => false)
    .finally(() => {
      refreshPromise = null
    })
  return refreshPromise
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const auth = opts.auth ?? true
  let token = auth ? useAuthStore.getState().accessToken : null
  let res = await doFetch(path, opts, token)

  if (res.status === 401 && auth) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      token = useAuthStore.getState().accessToken
      res = await doFetch(path, opts, token)
    } else {
      useAuthStore.getState().clear()
      throw await toApiError(res)
    }
  }

  if (!res.ok) throw await toApiError(res)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}
